import '@satellite/polyfills';

// Vue
import { createApp } from 'vue';
// APP
import App from './App.vue';
import config from './config/config';
import { initStore } from './store';

// Mixins
import localStorageMixin from '@satellite/components/mixins/localStorageMixin';
import utilityMixin from '@/components/mixins/utilityMixin';
import mappedStates from './components/mixins/mappedStates';
import mappedGetters from './components/mixins/mappedGetters';

// Composables
import { useLocaleHelpers } from '@satellite/composables/useLocaleHelpers';
import { analyticsKey } from '@satellite/composables/useAnalytics';
import { createLunaAnalyticsClient } from '@/analytics/client';

// Plugins
import util from '@/plugins/util';
import mirandaUtil from '@/plugins/MirandaUtil';
import ConfirmDialogPlugin from '@satellite/plugins/ConfirmDialog/ConfirmDialogPlugin';
import AxiosPlugin from '@satellite/plugins/AxiosPlugin';
import { HTTP_CLIENT_KEY } from '@satellite/composables/useHttpClient';
import VueQueryPluginInitializer from '@satellite/plugins/VueQueryPlugin';
import vuetify from './plugins/vuetifyPlugin';
import validation from '@satellite/plugins/Validator';
import initSentry, { captureSentryException } from '@satellite/plugins/sentry';
import Notifications from '@kyvg/vue3-notification';
import velocity from 'velocity-animate';
import i18n from './plugins/i18n';
import { setTranslator as setCoreTranslator, EntitySettings, uiAppName } from '@nova/core';
import { setTranslator } from '@satellite/plugins/translator';
import { registerVitePreloadErrorHandler } from '@/helpers/vite-preload-error.js';

// Feature Flags
import { plugin as featureFlagPlugin } from '@loadsmart/vue-feature-flags';
import featureFlagConfig from './plugins/featureFlagPlugin';

import initializeRouter from '@/routes.js';

// Deps
import '@mdi/font/css/materialdesignicons.css'; // Ensure you are using css-loader
import '@loadsmart/miranda-wc';
import mitt from 'mitt';
import VueTelInput from 'vue3-tel-input';
import 'vue3-tel-input/dist/vue3-tel-input.css';

// Helpers
import SocketPlugin from '@satellite/plugins/SocketPlugin';

import warehouseService from '@satellite/services/warehouse-service';
import userService from '@satellite/services/user-service';
import dockService from '@satellite/services/dock-service';
import companyService from '@satellite/services/company-service';
import loadtypeService from '@satellite/services/loadtype-service';
import appointmentService from '@satellite/services/appointment-service';
import carrierService from '@satellite/services/carrier-service';
import unitLimitService from '@satellite/services/unit-limits-service';

const app = createApp(App);

app.use(i18n);

// Init Plugins
AxiosPlugin.init(null, uiAppName.LUNA, app);
app.provide(HTTP_CLIENT_KEY, globalThis.axios);
VueQueryPluginInitializer.init(app);
ConfirmDialogPlugin.init(app);

const emitter = mitt();
emitter.$on = emitter.on;
emitter.$off = emitter.off;
emitter.$emit = emitter.emit;

// MODULES
// Vue.use(VueMask);
app.use(Notifications, { velocity });
app.use(featureFlagPlugin, { config: featureFlagConfig });
app.use(VueTelInput);

// Vue Prototypes
app.config.globalProperties.mirandaUtil = mirandaUtil;
app.config.globalProperties.util = util;
app.config.globalProperties.$validator = validation;
app.config.globalProperties.config = config;
app.config.globalProperties.$globalLoading = false;

// `notify` is invoked outside component context (e.g. `this.$app.config.globalProperties.notify(...)`
// from Vuex actions), so it needs `getNotificationTitle`/`$notify`/`$t`/`$te` permanently bound to
// `globalProperties` via `.bind()` - a plain reference would resolve `this` to whatever calls it.
app.config.globalProperties.getNotificationTitle = utilityMixin.methods.getNotificationTitle;
app.config.globalProperties.notify = utilityMixin.methods.notify.bind(app.config.globalProperties);

// Services
app.config.globalProperties.services = {
  warehouse: warehouseService,
  user: userService,
  dock: dockService,
  company: companyService,
  loadtype: loadtypeService,
  appointment: appointmentService,
  carrier: carrierService,
  unitLimit: unitLimitService
};

// Vue Global Mixins
app.mixin(utilityMixin);
app.mixin(localStorageMixin);
app.mixin(mappedStates);
app.mixin(mappedGetters);

const { getReadableLanguageFromLocale } = useLocaleHelpers();

initStore().then(async store => {
  app.use(store);
  app.config.globalProperties.sockets = new SocketPlugin(app);
  store.$app = app;

  const analyticsClient = createLunaAnalyticsClient();
  app.provide(analyticsKey, analyticsClient);
  // Escape hatch for plain-JS code that runs outside a component, e.g. the Auth/logout action.
  app.config.globalProperties.analytics = analyticsClient;
  analyticsClient.registerSuper({
    application_language: getReadableLanguageFromLocale(i18n.global.locale)
  });

  /**
   * Create a bound notify function for the store, e.g. `store.notify('message', 'success')`.
   */
  store.notify = (message, type = 'success', options = {}) => {
    app.config.globalProperties.notify(message, type, options);
  };

  const routes = await initializeRouter(store, app);
  app.use(routes);
  app.config.globalProperties.$eventHub = emitter;

  initSentry(app, routes, config);
  // Recover from stale/missing lazy CSS/JS chunks: capture to Sentry, then reload once per tab.
  registerVitePreloadErrorHandler({ captureException: captureSentryException });

  store.commit('Settings/setSettingsMetadata', EntitySettings);
  await store.dispatch('Auth/setInitialAccessToken');
  // ?handoff is owned exclusively by the router guard: the code is single-use (consumed once via
  // Redis GETDEL), so exchanging it here too would race the guard and consume it twice. Only the
  // legacy ?token is safe to pre-authenticate before mount (loginAs is idempotent).
  const urlToken = new URLSearchParams(window.location.search).get('token');
  if (urlToken && urlToken !== 'undefined') {
    try {
      await store.dispatch('Auth/loginAs', urlToken, 'loginPageWithTokenParam');
    } catch {
      // Invalid/expired token is non-fatal: keep booting so the router guard can redirect to login.
    }
  }
  store.commit('Auth/setPreviewWarehouseId', sessionStorage.getItem('previewWarehouseId'));

  // Import and use vuetify
  app.use(vuetify);

  setTranslator(i18n.global.t, i18n.global.locale);
  setCoreTranslator((key, params) => i18n.global.t(key, params));

  // Wait for the initial navigation (incl. the ?handoff exchange in the router guard) to resolve so
  // the shell paints logged-in, instead of flashing logged-out until the guard finishes.
  await routes.isReady();
  app.mount('#app');
});

export default app;
